import { useState } from "react";
import { AuthLayout } from "../../../components/layouts";
import {
    CountrySelectInput,
    PhoneNumberInput,
    StateSelectInput,
    TimezoneSelect,
} from "../../../components/ui";
import { Link, useNavigate } from "react-router-dom";
import AuthService from "../../../services/auth.service";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setUserInfo } from "../../../redux/reducers/authSlice";
import { MigrationPopup } from "../../../components/custom-hooks/migrationPopUp";
import Modal from "../../../components/ui/Model";
import { Lock, Mail } from "lucide-react";
import { PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY } from "../../../constants/sessionStorageKeys";
import AuthInput from "../../../components/ui/AuthInput";
import AuthPrimaryButton from "../../../components/ui/AuthPrimaryButton";

const initialValues = {
    email: '',
    password: ''
}

export default function SignIn() {
    const authService = new AuthService();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(initialValues);
    const [loading, setLoading] = useState(false);
    const [migration, setMigration] = useState(false);
    const [isMigrated, setIsMigrated] = useState(false);
    const [id, setId] = useState();
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [upgradeEmail, setUpgradeEmail] = useState("");
    const [upgradeCountry, setUpgradeCountry] = useState("");
    const [upgradeState, setUpgradeState] = useState("");
    const [upgradeTimezone, setUpgradeTimezone] = useState("");
    const [upgradePhone, setUpgradePhone] = useState("");
    const [upgradeCountryObj, setUpgradeCountryObj] = useState<any>(null);
    const [loginResponse, setLoginResponse] = useState<any>(null);

    const gridContainer = `w-full grid gap-1`;
    const labelStyle = `text-ryd-subTextPrimary font-[400] text-[13px] leading-[26px]`;
    const upgradeInputStyle = `w-full bg-ryd-gray rounded-[16px] text-[14px] leading-[26px] font-[400] text-[#576877] px-[26px] py-[12px] outline-none active:outline-none`;

    const resetUpgradeForm = () => {
        setUpgradeCountry("");
        setUpgradeState("");
        setUpgradeTimezone("");
        setUpgradePhone("");
        setUpgradeCountryObj(null);
    };

    const upgradeFormReady = Boolean(
        upgradeCountry.trim() &&
        upgradeState.trim() &&
        upgradeTimezone.trim() &&
        upgradePhone.trim()
    );

    const handleUpgradeCountryChange = (data: any) => {
        setUpgradeCountryObj(data);
        setUpgradeCountry(data?.name || "");
        setUpgradeState("");
        setUpgradeTimezone("");
        setUpgradePhone("");
    };

    const handleUpgradeStateChange = (data: any) => {
        setUpgradeState(data?.name || "");
    };

    const handleUpgradeTimezoneChange = (data: any) => {
        setUpgradeTimezone(data?.zoneName || "");
    };

    const handleUpgradePhoneChange = (data: string) => {
        setUpgradePhone(data);
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await authService.signIn(formData);
            setLoading(false);

            if (!response.status) {
                if (response?.data?.upgradeEligible) {
                    setUpgradeEmail(response?.data?.email || formData.email);
                    resetUpgradeForm();
                    setShowUpgradeModal(true);
                    return;
                }
                toast.error(response?.message);
                return;
            }

            if (response.data?.userType === "BOTH") {
                setLoginResponse(response);
                setShowRoleModal(true);
                return;
            }

            handleLoginSuccess(response);
        } catch (err: any) {
            setLoading(false);
            toast.error(err.message);
        }
    };

    const handleUpgradeToNormal = async () => {
        if (!upgradeCountry.trim() || !upgradeState.trim() || !upgradeTimezone.trim() || !upgradePhone.trim()) {
            toast.error("Country, state, timezone and phone number are required.");
            return;
        }
        setUpgrading(true);
        try {
            const response = await authService.upgradeToNormal({
                email: upgradeEmail || formData.email,
                password: formData.password,
                country: upgradeCountry.trim(),
                state: upgradeState.trim(),
                timezone: upgradeTimezone.trim(),
                phone: upgradePhone.trim(),
            });

            if (!response?.status) {
                toast.error(response?.message || "Unable to upgrade account.");
                return;
            }

            toast.success(response?.message || "Account upgraded successfully.");
            setShowUpgradeModal(false);
            resetUpgradeForm();
            const loginRes = await authService.signIn(formData);
            if (loginRes?.status) {
                handleLoginSuccess(loginRes);
            } else {
                toast.info("Please sign in again with your credentials.");
            }
        } catch (err: any) {
            toast.error(err?.message || "Unable to upgrade account.");
        } finally {
            setUpgrading(false);
        }
    };

    const handleLoginSuccess = (response: any) => {
        if (response?.userType === "PARTNER_PARENT") {
            window.location.href = `https://partners.rydlearning.com/parent/validation/${response.data.token}`;
            return;
        }
        localStorage.setItem('ryd-parent-token', response?.data?.token);
        try {
            sessionStorage.removeItem(PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY);
        } catch {
            /* ignore */
        }
        toast.success(response?.message);
        dispatch(setUserInfo(response?.data));
        navigate('/parent/home');
    };

    const handleRoleSelection = (role: "NORMAL_PARENT" | "PARTNER_PARENT") => {
        setShowRoleModal(false);
        handleLoginSuccess({
            ...loginResponse,
            data: loginResponse.data,
            userType: role
        });
    };

    return (
        <AuthLayout>
            {migration && !isMigrated && <MigrationPopup id={id} />}

            <Modal
                isOpen={showRoleModal}
                onClose={() => setShowRoleModal(false)}
                title="Select Login Type"
                closeOnOverlayClick={false}
            >
                <div className="p-4">
                    <p className="mb-4 text-[14px] leading-relaxed text-[#476788]">
                        You have both Normal Parent and Partner Parent accounts. Which one would you like to use?
                    </p>
                    <div className="flex flex-col gap-2.5 sm:flex-row">
                        <button
                            onClick={() => handleRoleSelection("NORMAL_PARENT")}
                            className="w-full rounded-xl bg-ryd-primary px-4 py-2.5 text-white sm:w-auto"
                        >
                            Parent (Individual)
                        </button>
                        <button
                            onClick={() => handleRoleSelection("PARTNER_PARENT")}
                            className="w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-white sm:w-auto"
                        >
                            Parent (Corporate)
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showUpgradeModal}
                onClose={() => {
                    setShowUpgradeModal(false);
                    resetUpgradeForm();
                }}
                title="Upgrade Required"
                closeOnOverlayClick={false}
                hideHeader
                panelClassName="!p-0 max-w-[min(560px,calc(100vw-1.5rem))] rounded-2xl overflow-visible shadow-2xl"
            >
                <div data-testid="upgrade-modal">
                    <div className="relative border-b border-[#F0F2F5] bg-gradient-to-br from-ryd-primaryLess1 via-white to-white px-4 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-6">
                        <button
                            type="button"
                            onClick={() => {
                                setShowUpgradeModal(false);
                                resetUpgradeForm();
                            }}
                            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-2xl leading-none text-gray-400 hover:bg-white hover:text-gray-700 sm:right-4 sm:top-4"
                            aria-label="Close"
                            disabled={upgrading}
                        >
                            ×
                        </button>
                        <p className="mb-2 text-[12px] font-[500] uppercase tracking-[0.08em] text-ryd-primary">
                            Account upgrade
                        </p>
                        <h3 className="mb-2 pr-8 font-[AvertaStd-Semibold] text-[20px] leading-tight text-ryd-headerTextPrimary sm:text-[22px]">
                            Unlock Live Interaction Classes
                        </h3>
                        <p className="max-w-[440px] text-[13px] leading-[22px] text-ryd-subTextSecondary sm:text-[14px]">
                            This account is currently AI Tutor only. Add your location details below to continue into the Live Interaction Class dashboard.
                        </p>
                        {upgradeEmail ? (
                            <p className="mt-3 inline-flex max-w-full items-center truncate rounded-full border border-[#E6EBF0] bg-white px-3 py-1 text-[12px] text-ryd-subTextSecondary1">
                                {upgradeEmail}
                            </p>
                        ) : null}
                    </div>

                    <div className="px-4 py-4 sm:px-6 sm:py-5">
                        <p className="mb-4 text-[12px] text-ryd-subTextSecondary">
                            Select a country first — state, phone code, and timezone update automatically.
                        </p>
                        <div className="mb-6 grid gap-4 md:grid-cols-2">
                            <div className={gridContainer}>
                                <label className={labelStyle}>Country</label>
                                <CountrySelectInput
                                    handleCountryChange={handleUpgradeCountryChange}
                                    className={upgradeInputStyle}
                                />
                            </div>
                            <div className={gridContainer}>
                                <label className={labelStyle}>Province / State</label>
                                <StateSelectInput
                                    key={`state-${upgradeCountryObj?.name || "none"}`}
                                    country={upgradeCountryObj}
                                    handleStateChange={handleUpgradeStateChange}
                                    className={upgradeInputStyle}
                                />
                            </div>
                            <div className={gridContainer}>
                                <label className={labelStyle}>Phone number</label>
                                <PhoneNumberInput
                                    key={`phone-${upgradeCountryObj?.name || "none"}`}
                                    country={upgradeCountryObj}
                                    handlePhoneInputChange={handleUpgradePhoneChange}
                                    className={upgradeInputStyle}
                                />
                            </div>
                            <div className={gridContainer}>
                                <label className={labelStyle}>Study timezone</label>
                                <TimezoneSelect
                                    key={`tz-${upgradeCountryObj?.name || "none"}`}
                                    country={upgradeCountryObj}
                                    handleTimezoneChange={handleUpgradeTimezoneChange}
                                    className={upgradeInputStyle}
                                    placeholder="- select timezone -"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowUpgradeModal(false);
                                    resetUpgradeForm();
                                }}
                                className="rounded-[12px] border border-[#E6EBF0] px-5 py-2.5 text-ryd-subTextPrimary transition-colors hover:bg-ryd-gray"
                                disabled={upgrading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                data-testid="upgrade-submit"
                                onClick={handleUpgradeToNormal}
                                className="rounded-[12px] bg-ryd-primary px-5 py-2.5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={upgrading || !upgradeFormReady}
                            >
                                {upgrading ? "Upgrading..." : "Upgrade & continue"}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            <div className="mb-6 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-ryd-primaryLess1 text-ryd-primary">
                    <Lock size={20} />
                </div>
                <h1 className="font-[AvertaStd-Semibold] text-[22px] text-ryd-headerTextPrimary sm:text-[26px]">
                    Welcome back! 👋
                </h1>
                <p className="mt-1.5 px-1 text-[13px] text-[#8b9aab] sm:text-[14px]">
                    Sign in to continue your learning journey.
                </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
                <AuthInput
                    label="Email Address"
                    type="email"
                    placeholder="example@email.com"
                    required
                    leftIcon={<Mail size={18} />}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />

                <div>
                    <AuthInput
                        label="Password"
                        isPassword
                        placeholder="Enter your password"
                        required
                        leftIcon={<Lock size={18} />}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    <div className="mt-2 flex justify-end">
                        <Link
                            to="/parent/forgot-psd"
                            className="text-[13px] font-semibold text-ryd-primary hover:underline"
                        >
                            Forgot Password?
                        </Link>
                    </div>
                </div>

                <AuthPrimaryButton loading={loading}>Sign In</AuthPrimaryButton>

                <p className="pt-1 text-center text-[14px] text-[#8b9aab]">
                    Don&apos;t have an account?{' '}
                    <Link to="/parent/sign-up" className="font-semibold text-ryd-primary hover:underline">
                        Create Account
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
}
